import type { IndicatorCatalog, IndicatorDefinition, ParamSchema } from './types'

/* ------------------------------------------------------------------ */
/*  Palette                                                             */
/* ------------------------------------------------------------------ */
/* These match the klinecharts default dark-theme palette so the       */
/* "no-style-override" state visually matches what the chart already   */
/* renders today. Editors that surface "Reset to defaults" return the  */
/* user to these.                                                       */
const PALETTE = {
  ma1: '#FF9600',
  ma2: '#935EBD',
  ma3: '#2196F3',
  ma4: '#00BCD4',
  ma5: '#26A69A',
  up: '#089981',
  down: '#F23645',
  mid: '#FFB74D',
  upper: '#F23645',
  lower: '#089981',
  signal: '#FF9600',
  macd: '#2962FF',
  histPos: '#089981',
  histNeg: '#F23645',
  vol: '#FFB74D',
  k: '#FF9600',
  d: '#935EBD',
  j: '#2196F3',
  rsi: '#FF9600',
  bull: '#089981',
  bear: '#F23645',
  neutral: '#787B86',
} as const

// Deliberately NOT a klinecharts-palette hue: the volume moving average reads
// as calm chrome above the bars rather than a loud accent. Kept separate from
// PALETTE (which mirrors klinecharts defaults) so it's clearly an app choice.
const VOL_MA_COLOR = '#d1d5db'

// MACD histogram: strong colour while momentum builds away from zero, pale
// while it fades back toward it.
const MACD_HIST = {
  growAbove: '#26A69A',
  fallAbove: '#B2DFDB',
  growBelow: '#FFCDD2',
  fallBelow: '#FF5252',
} as const

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

function periodParam(key: string, label: string, def: number, max = 500) {
  return {
    type: 'int' as const,
    key,
    label,
    default: def,
    min: 1,
    max,
    step: 1,
    unit: 'bars',
  }
}

function line(
  key: string,
  label: string,
  color: string,
  opts?: { hideable?: boolean; opacity?: number; style?: 'solid' | 'dashed' | 'dotted' },
) {
  return {
    key,
    label,
    type: 'line' as const,
    defaultColor: color,
    defaultSize: 1,
    defaultStyle: opts?.style ?? ('solid' as const),
    ...(opts?.hideable ? { hideable: true } : {}),
    ...(opts?.opacity !== undefined ? { defaultOpacity: opts.opacity } : {}),
  }
}

/* ------------------------------------------------------------------ */
/*  Main-pane indicators                                                */
/* ------------------------------------------------------------------ */

const MA: IndicatorDefinition = {
  name: 'MA',
  label: 'Moving Average',
  description: 'Simple moving average across the closing price.',
  paneType: 'main',
  params: [periodParam('period', 'Period', 20)],
  // klinecharts MA emits one line per param when calcParams has multiple
  // entries. Our catalog defaults to a single line; figures match that.
  figures: [line('ma1', 'MA', PALETTE.ma1)],
}

const EMA: IndicatorDefinition = {
  name: 'EMA',
  label: 'Exponential Moving Average',
  description: 'Exponential moving average, weighting recent prices.',
  paneType: 'main',
  params: [periodParam('period', 'Period', 20)],
  figures: [line('ema1', 'EMA', PALETTE.ma2)],
}

const SMA: IndicatorDefinition = {
  name: 'SMA',
  label: 'Smoothed Moving Average',
  description: 'Smoothed moving average with adjustable weight.',
  paneType: 'main',
  params: [periodParam('period', 'Period', 20), periodParam('weight', 'Weight', 1, 100)],
  figures: [line('sma1', 'SMA', PALETTE.ma3)],
}

const BOLL: IndicatorDefinition = {
  name: 'BOLL',
  label: 'Bollinger Bands',
  description: 'Standard deviation bands around a moving average.',
  paneType: 'main',
  params: [
    periodParam('period', 'Period', 20),
    {
      type: 'float',
      key: 'stddev',
      label: 'Std Dev',
      default: 2,
      min: 0.1,
      max: 10,
      step: 0.1,
      unit: 'σ',
    },
  ],
  figures: [
    line('up', 'Upper', PALETTE.upper),
    line('mid', 'Middle', PALETTE.mid),
    line('dn', 'Lower', PALETTE.lower),
  ],
}

const SAR: IndicatorDefinition = {
  name: 'SAR',
  label: 'Parabolic SAR',
  description: 'Trend-following stop-and-reverse dots.',
  paneType: 'main',
  // klinecharts' built-in SAR reads calcParams as [start, step, max], each an
  // acceleration factor in hundredths (2 = 0.02). The slot order IS that order:
  // saved layouts, saved defaults and the AI's indicator tool all write by index.
  params: [
    { ...periodParam('start', 'Start', 2, 50), unit: '%' },
    { ...periodParam('step', 'Step', 2, 50), unit: '%' },
    { ...periodParam('max', 'Max', 20, 200), unit: '%' },
  ],
  figures: [
    {
      key: 'sar',
      label: 'SAR',
      type: 'circle',
      defaultColor: PALETTE.up,
      defaultUpColor: PALETTE.up,
      defaultDownColor: PALETTE.down,
      defaultSize: 1,
    },
  ],
}

const BBI: IndicatorDefinition = {
  name: 'BBI',
  label: 'Bull/Bear Index',
  description: 'Average of four MAs (default 3, 6, 12, 24).',
  paneType: 'main',
  params: [
    periodParam('p1', 'MA 1', 3, 200),
    periodParam('p2', 'MA 2', 6, 200),
    periodParam('p3', 'MA 3', 12, 200),
    periodParam('p4', 'MA 4', 24, 500),
  ],
  figures: [line('bbi', 'BBI', PALETTE.ma1)],
}

const VWAP: IndicatorDefinition = {
  name: 'VWAP',
  label: 'VWAP',
  description: 'Volume-weighted average price, anchored to the session.',
  paneType: 'main',
  // Both default to the long-standing behavior, so a VWAP saved before these
  // inputs existed (no calcParams) draws exactly as it did.
  params: [
    {
      type: 'enum',
      key: 'anchor',
      label: 'Session start',
      description: "Where each day's VWAP starts accumulating (times are US Eastern).",
      default: 'premarket',
      options: [
        { value: 'premarket', label: 'Premarket (4:00)' },
        { value: 'regular', label: 'Regular open (9:30)' },
      ],
    },
    {
      type: 'bool',
      key: 'sessionBreaks',
      label: 'Break line between sessions',
      description: 'Start each day as a separate line instead of joining it to the day before.',
      default: false,
    },
  ],
  figures: [line('vwap', 'VWAP', PALETTE.ma3)],
}

const PIVOT: IndicatorDefinition = {
  name: 'PIVOT',
  label: 'Pivot Points',
  fixedPlotTypes: true,
  description:
    'Standard (floor) pivot points: pivot plus three support and three resistance levels, derived from the previous period’s high/low/close.',
  paneType: 'main',
  params: [],
  // Every level is individually hideable (disable R3/S3, etc.) — the custom
  // pivot draw honors the per-figure `visible` override.
  figures: [
    line('p', 'P', PALETTE.mid, { hideable: true }),
    line('r1', 'R1', PALETTE.bear, { hideable: true }),
    line('r2', 'R2', PALETTE.bear, { hideable: true }),
    line('r3', 'R3', PALETTE.bear, { hideable: true }),
    line('s1', 'S1', PALETTE.bull, { hideable: true }),
    line('s2', 'S2', PALETTE.bull, { hideable: true }),
    line('s3', 'S3', PALETTE.bull, { hideable: true }),
  ],
}

const POSITION_SIZE: IndicatorDefinition = {
  name: 'POSITION_SIZE',
  label: 'Position Sizing',
  fixedPlotTypes: true,
  description:
    'A sizing readout, not a plotted series: pick a direction, a dollar risk, and a reference-level source, and the pill shows the share size implied by the distance from the last price to the nearest RELEVANT level — long needs its stop below price, short above. Manual mode auto-binds to your own horizontal lines and rays. A dash means no relevant level: "(wrong side)" when levels exist but none is adverse, "(no data)" when the source has no value yet.',
  paneType: 'main',
  params: [
    {
      type: 'enum',
      key: 'direction',
      label: 'Direction',
      description: 'Long sizes against a stop BELOW price; short against one ABOVE.',
      default: 'long',
      options: [
        { value: 'long', label: 'Long' },
        { value: 'short', label: 'Short' },
      ],
    },
    {
      type: 'float',
      key: 'risk',
      label: 'Risk',
      description:
        'Dollars at risk if the stop is hit. Shares are floored so realized risk never exceeds this.',
      default: 100,
      min: 1,
      max: 1_000_000,
      step: 1,
      unit: '$',
    },
    {
      type: 'enum',
      key: 'source',
      label: 'Stop source',
      description:
        'Where the stop level comes from. Manual follows your own horizontal lines/rays; Pivot binds the nearest floor-pivot level; the rest are single levels.',
      default: 'manual',
      options: [
        { value: 'manual', label: 'My lines' },
        { value: 'pivot', label: 'Pivot points' },
        { value: 'pdh', label: 'Prev day high' },
        { value: 'pdl', label: 'Prev day low' },
        { value: 'sma', label: 'SMA' },
        { value: 'ema', label: 'EMA' },
        { value: 'vwap', label: 'VWAP' },
        { value: 'atr', label: 'ATR' },
      ],
    },
    {
      type: 'int',
      key: 'maPeriod',
      label: 'Period',
      description:
        'Length of the moving average the stop binds to, or the ATR lookback (stop = price minus ATR long, plus ATR short).',
      // Slot 2 is `source`. Offering a period next to "My lines" or
      // "Prev day high" just invites the question of what it does.
      showWhen: { index: 2, in: ['sma', 'ema', 'atr'] },
      default: 9,
      min: 1,
      max: 500,
      step: 1,
      unit: 'bars',
    },
    {
      type: 'float',
      key: 'maxPosition',
      // Appended last in calcParams for back-compat, but it belongs next to
      // Risk in the form: the two together are the whole sizing budget.
      order: 0.5,
      label: 'Max position',
      description:
        'Ceiling on capital deployed (price x shares). When it binds it overrides the risk-derived size, and the readout says so. 0 means no ceiling.',
      default: 0,
      min: 0,
      max: 100_000_000,
      step: 1000,
      unit: '$',
    },
  ],
  // The registered template keeps `figures: []` so the stop level never
  // stretches the price pane's y-axis; the catalog declares the line here so
  // the Style tab lights up and buildIndicatorStyles emits styles.lines[0]
  // for the custom draw (the VPVR/HTF pattern). Dashed red by default so it
  // reads as a stop, not another traded series.
  figures: [line('stop', 'Stop', PALETTE.bear, { style: 'dashed', hideable: true })],
}

const VPVR: IndicatorDefinition = {
  name: 'VPVR',
  label: 'Volume Profile',
  fixedPlotTypes: true,
  description:
    'Visible-range volume profile: a histogram of volume-at-price with point of control, value area, and an optional buy/sell split.',
  paneType: 'main',
  params: [
    { type: 'int', key: 'rows', label: 'Rows', default: 24, min: 10, max: 100, step: 1 },
    {
      type: 'int',
      key: 'widthPct',
      label: 'Width',
      default: 30,
      min: 10,
      max: 60,
      step: 1,
      unit: '%',
    },
    {
      type: 'int',
      key: 'valueAreaPct',
      label: 'Value Area',
      default: 70,
      min: 50,
      max: 90,
      step: 1,
      unit: '%',
    },
    { type: 'bool', key: 'split', label: 'Buy/Sell Split', default: true },
    {
      type: 'enum',
      key: 'placement',
      label: 'Placement',
      default: 'left',
      options: [
        { value: 'left', label: 'Left' },
        { value: 'right', label: 'Right' },
      ],
    },
  ],
  // Style-only figures: the registered indicator keeps `figures: []` (volume
  // magnitudes must not enter the price y-axis) and paints via a custom draw.
  // These exist so the Style tab offers color pickers; buildIndicatorStyles
  // emits them into `styles.lines[]` in THIS order and the draw reads that
  // array positionally — profile, up, down, poc.
  // Softer, desaturated fills so the profile (painted BEHIND the candles) reads
  // as a quiet backdrop rather than competing with the bars. These must match
  // the hardcoded fallbacks in charts-core `volumeProfileIndicator.ts` (that
  // package can't import this catalog), so keep the two in sync.
  figures: [
    line('profile', 'Profile', '#6E7480'),
    line('up', 'Up Volume', '#4C8B82'),
    line('down', 'Down Volume', '#B05C68'),
    line('poc', 'POC', PALETTE.mid),
  ],
}

// Session Volume Profile: identical controls to VPVR, but the profile is
// computed PER TRADING DAY from the whole session and pinned inside that day's
// column span. Same param + figure ORDER so the charts-core draw reads them
// identically; only the placement default differs — sessions pin to their START
// (open) edge by default, the classic SVP look. Placement labels are relabelled
// to the session's own edges (Start/End) rather than chart Left/Right.
const VPSV: IndicatorDefinition = {
  name: 'VPSV',
  label: 'Volume Profile (Session)',
  fixedPlotTypes: true,
  description:
    'Session volume profile: an independent volume-at-price histogram for each trading day (computed from the full session), pinned within that session’s columns, with point of control, value area, and an optional buy/sell split.',
  paneType: 'main',
  params: [
    ...VPVR.params.map((p) =>
      p.key === 'placement'
        ? ({
            type: 'enum',
            key: 'placement',
            label: 'Placement',
            description: 'Which edge of the session the histogram hugs.',
            default: 'left',
            options: [
              { value: 'left', label: 'Session Start' },
              { value: 'right', label: 'Session End' },
            ],
          } satisfies ParamSchema)
        : p,
    ),
    // Positional — must stay the LAST param, matching calcParams[5] in
    // charts-core/utils/volumeProfileIndicator.ts.
    {
      type: 'enum',
      key: 'sessionAnchor',
      label: 'Session starts',
      description:
        'Small caps often move on news released after the close. Anchoring to the prior after-hours keeps that volume with the session it drives, instead of splitting it onto the day that already ended.',
      default: 'calendar-day',
      options: [
        { value: 'calendar-day', label: 'Pre-market (calendar day)' },
        { value: 'prior-after-hours', label: 'Prior day after-hours' },
      ],
    },
  ],
  figures: VPVR.figures,
}

// Volume Profile (X Days): one pane-spanning profile over the last N session
// days of loaded data, anchored to the newest bar rather than the viewport. Same
// first five params + figure ORDER as VPVR so the charts-core draw reads them
// identically; `days` and `sessionAnchor` are appended as calcParams[5] and [6].
const VPXD: IndicatorDefinition = {
  name: 'VPXD',
  label: 'Volume Profile (X Days)',
  fixedPlotTypes: true,
  description:
    'Rolling volume profile over the last N trading days, measured from the most recent bar so it does not change as you pan or zoom. Built for day-2 small caps, where the levels that matter were set by the last session or two.',
  paneType: 'main',
  params: [
    ...VPVR.params,
    // Positional — `days` is calcParams[5] and `sessionAnchor` calcParams[6] in
    // charts-core/utils/volumeProfileIndicator.ts. Order is load-bearing.
    {
      type: 'int',
      key: 'days',
      label: 'Days',
      description:
        'How many trading days back the profile reaches, counting the current (possibly in-progress) day as one.',
      default: 2,
      min: 1,
      max: 30,
      step: 1,
    },
    {
      type: 'enum',
      key: 'sessionAnchor',
      label: 'Day starts',
      description:
        'Small caps often move on news released after the close. Anchoring to the prior after-hours keeps that volume with the session it drives, instead of splitting it onto the day that already ended.',
      default: 'calendar-day',
      options: [
        { value: 'calendar-day', label: 'Midnight (calendar day)' },
        { value: 'prior-after-hours', label: 'Prior day close' },
      ],
    },
    {
      type: 'enum',
      key: 'windowAnchor',
      label: 'Measured from',
      description:
        'Latest data never moves as you pan. Visible edge counts the days back from the right of the screen, so scrolling a past day into view profiles THAT day — identical to Latest data while you are at the live edge.',
      default: 'latest-data',
      options: [
        { value: 'latest-data', label: 'Latest data' },
        { value: 'visible', label: 'Visible edge' },
      ],
    },
  ],
  figures: VPVR.figures,
}

/**
 * Higher-timeframe projection. Params are positional: [0] target period,
 * [1] field — matching charts-core/utils/htfIndicator.ts.
 *
 * The registered indicator carries `figures: []` so it stays OUT of the price-axis
 * range; this `line` figure exists only so the Style tab offers colour. The draw
 * reads `styles.lines[0]` positionally — the same split VPVR uses.
 */
const HTF: IndicatorDefinition = {
  name: 'HTF',
  label: 'Higher Timeframe',
  fixedPlotTypes: true,
  description:
    'Projects a coarser intraday timeframe onto the chart as a stepped line — the 1H close on a 5m chart, for example. Deliberately does not affect the price scale, so a distant level cannot squash the intraday detail; it is simply not drawn when off-screen.',
  paneType: 'main',
  params: [
    {
      type: 'enum',
      key: 'target',
      label: 'Timeframe',
      timeframeParam: true,
      description:
        'Projects this timeframe onto the chart. Intraday only — a daily bucket would start at UTC midnight rather than the session open.',
      default: '1H',
      options: [
        { value: '15m', label: '15 minutes' },
        { value: '30m', label: '30 minutes' },
        { value: '1H', label: '1 hour' },
        { value: '4H', label: '4 hours' },
      ],
    },
    {
      type: 'enum',
      key: 'display',
      hidden: true,
      label: 'Display',
      description:
        'Candles draws the whole coarser bar — a saturated body from open to close over a faint wick range. Line draws a stepped level of one field.',
      default: 'candles',
      options: [
        { value: 'candles', label: 'Candles' },
        { value: 'line', label: 'Line' },
      ],
    },
    {
      type: 'enum',
      key: 'field',
      hidden: true,
      label: 'Field',
      description: 'Which value the stepped Line draws. Ignored in Candles mode.',
      default: 'close',
      options: [
        { value: 'close', label: 'Close' },
        { value: 'open', label: 'Open' },
        { value: 'high', label: 'High' },
        { value: 'low', label: 'Low' },
      ],
    },
    // Slot retained for old persisted arrays; visibility now lives on the
    // `wick` figure so Body/Border/Wicks sit together in the Style tab.
    { type: 'bool', key: 'wicks', label: 'Show wicks', default: true, hidden: true },
    // Positional calcParams[4]. Whether to draw the currently-building (not yet
    // closed) higher-timeframe bar. On by default (it renders dimmed, as a
    // provisional level); off hides it so only completed buckets show.
    {
      type: 'bool',
      key: 'plotIncomplete',
      label: 'Plot incomplete bars',
      description:
        'Draw the higher-timeframe bar that is still building — the current, not-yet-closed bucket. It renders dimmed because its values repaint as new bars arrive. Turn off to show only completed bars.',
      default: true,
    },
  ],
  // Positional: [0] up, [1] down, [2] border — the draw reads styles.lines by
  // index, the same contract VPVR uses. Line mode takes its stroke from [0].
  // Border and Wicks are up/down PAIRS like the chart settings' candle rows.
  // The empty defaultColor is a follow-the-body sentinel: unstyled, each keeps
  // the body's own up/down tint (the original look). Visibility rides the
  // pair's `up` figure. Positional: [2] borderUp [3] borderDown [4] wickUp
  // [5] wickDown.
  figures: [
    line('up', 'Up', '#26A69A'),
    line('down', 'Down', '#EF5350'),
    line('borderUp', 'Border', '', { hideable: true }),
    line('borderDown', 'Border (down)', ''),
    line('wickUp', 'Wicks', '', { hideable: true }),
    line('wickDown', 'Wicks (down)', ''),
  ],
}

/* ------------------------------------------------------------------ */
/*  Extended MA family + bands (registered in charts-core              */
/*  utils/maBandIndicators.ts — param order must match calcParams)     */
/* ------------------------------------------------------------------ */

function maDef(
  name: string,
  label: string,
  description: string,
  figureKey: string,
  defaultPeriod: number,
  color: string,
): IndicatorDefinition {
  return {
    name,
    label,
    description,
    paneType: 'main',
    params: [periodParam('period', 'Period', defaultPeriod)],
    figures: [line(figureKey, name, color)],
  }
}

const WMA = maDef(
  'WMA',
  'Weighted Moving Average',
  'Linearly weighted moving average — recent bars weigh more.',
  'wma',
  20,
  PALETTE.ma1,
)

const HMA = maDef(
  'HMA',
  'Hull Moving Average',
  'Hull moving average — heavily smoothed with near-zero lag.',
  'hma',
  16,
  PALETTE.ma2,
)

const DEMA = maDef(
  'DEMA',
  'Double Exponential Moving Average',
  'Double EMA — subtracts the EMA-of-EMA to cut lag.',
  'dema',
  20,
  PALETTE.ma3,
)

const TEMA = maDef(
  'TEMA',
  'Triple Exponential Moving Average',
  'Triple EMA — third-order lag compensation.',
  'tema',
  20,
  PALETTE.ma4,
)

const TMA = maDef(
  'TMA',
  'Triangular Moving Average',
  'Triangular moving average — double-smoothed SMA, center-weighted.',
  'tma',
  20,
  PALETTE.ma5,
)

const KAMA = maDef(
  'KAMA',
  'Kaufman Adaptive Moving Average',
  'Adapts smoothing between fast and slow by trend efficiency.',
  'kama',
  10,
  PALETTE.ma1,
)

const VIDYA: IndicatorDefinition = {
  name: 'VIDYA',
  label: 'Variable Index Dynamic Average',
  description: 'Chande’s adaptive EMA — smoothing scales with |CMO| momentum.',
  paneType: 'main',
  params: [periodParam('period', 'Period', 14), periodParam('cmo', 'CMO Period', 9, 100)],
  figures: [line('vidya', 'VIDYA', PALETTE.ma2)],
}

const ZLEMA = maDef(
  'ZLEMA',
  'Zero-Lag Exponential Moving Average',
  'EMA of a de-lagged price series.',
  'zlema',
  14,
  PALETTE.ma3,
)

const MGD = maDef(
  'MGD',
  'McGinley Dynamic',
  'Self-adjusting average that speeds up in downmoves and hugs price.',
  'mgd',
  14,
  PALETTE.ma4,
)

const WWS = maDef(
  'WWS',
  'Welles Wilder Smoothing',
  'Wilder’s smoothing (the RSI/ATR average) applied to close.',
  'wws',
  14,
  PALETTE.ma5,
)

const bandFigureDefs = [
  line('up', 'Upper', PALETTE.upper),
  line('mid', 'Middle', PALETTE.mid),
  line('dn', 'Lower', PALETTE.lower),
]

const KC: IndicatorDefinition = {
  name: 'KC',
  label: 'Keltner Channel',
  description: 'EMA midline with ATR-multiple bands.',
  paneType: 'main',
  params: [
    periodParam('period', 'EMA Period', 20),
    periodParam('atr', 'ATR Period', 10, 200),
    {
      type: 'float',
      key: 'mult',
      label: 'Multiplier',
      default: 2,
      min: 0.1,
      max: 10,
      step: 0.1,
      unit: '×ATR',
    },
  ],
  figures: bandFigureDefs,
}

const DC: IndicatorDefinition = {
  name: 'DC',
  label: 'Donchian Channel',
  description: 'Highest high and lowest low over the lookback, with midline.',
  paneType: 'main',
  params: [periodParam('period', 'Period', 20)],
  figures: bandFigureDefs,
}

const ENV: IndicatorDefinition = {
  name: 'ENV',
  label: 'MA Envelopes',
  description: 'Simple MA midline offset up and down by a fixed percent.',
  paneType: 'main',
  params: [
    periodParam('period', 'Period', 20),
    {
      type: 'float',
      key: 'percent',
      label: 'Envelope',
      default: 2.5,
      min: 0.1,
      max: 25,
      step: 0.1,
      unit: '%',
    },
  ],
  figures: bandFigureDefs,
}

const HLB: IndicatorDefinition = {
  name: 'HLB',
  label: 'High Low Bands',
  description: 'Triangular moving averages of the highs and lows.',
  paneType: 'main',
  params: [periodParam('period', 'Period', 14)],
  figures: bandFigureDefs,
}

const APZ: IndicatorDefinition = {
  name: 'APZ',
  label: 'Adaptive Price Zone',
  description: 'Double-EMA midline with bands scaled by double-EMA bar range.',
  paneType: 'main',
  params: [
    periodParam('period', 'Period', 21),
    {
      type: 'float',
      key: 'band',
      label: 'Band Width',
      default: 2,
      min: 0.1,
      max: 10,
      step: 0.1,
      unit: '×range',
    },
  ],
  figures: bandFigureDefs,
}

/* ------------------------------------------------------------------ */
/*  Linear regression family (registered in charts-core                */
/*  utils/talibIndicators.ts)                                          */
/* ------------------------------------------------------------------ */

const LINREG: IndicatorDefinition = {
  name: 'LINREG',
  label: 'Linear Regression',
  description: 'Rolling least-squares fit evaluated at the current bar.',
  paneType: 'main',
  params: [periodParam('period', 'Period', 14)],
  figures: [line('linreg', 'LINREG', PALETTE.ma1)],
}

const LRI: IndicatorDefinition = {
  name: 'LRI',
  label: 'Linear Regression Intercept',
  description: 'Rolling least-squares fit evaluated at the window start.',
  paneType: 'main',
  params: [periodParam('period', 'Period', 14)],
  figures: [line('intercept', 'Intercept', PALETTE.ma2)],
}

const TSF: IndicatorDefinition = {
  name: 'TSF',
  label: 'Time Series Forecast',
  description: 'Least-squares fit projected one bar ahead (a.k.a. Linear Regression Forecast).',
  paneType: 'main',
  params: [periodParam('period', 'Period', 14)],
  figures: [line('tsf', 'TSF', PALETTE.ma3)],
}

const LRS: IndicatorDefinition = {
  name: 'LRS',
  label: 'Linear Regression Slope',
  description: 'Slope of the rolling least-squares fit, in price units per bar.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 14)],
  figures: [line('slope', 'Slope', PALETTE.ma1)],
}

const LRA: IndicatorDefinition = {
  name: 'LRA',
  label: 'Linear Regression Angle',
  description: 'Slope of the rolling least-squares fit expressed in degrees.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 14)],
  figures: [line('angle', 'Angle', PALETTE.ma1)],
}

/* ------------------------------------------------------------------ */
/*  Sub-pane indicators                                                 */
/* ------------------------------------------------------------------ */

const VOL: IndicatorDefinition = {
  name: 'VOL',
  label: 'Volume',
  description: 'Volume histogram with optional MA overlay.',
  paneType: 'sub',
  // A single volume moving average. One param → `volumeIndicator.ts`
  // regenerateFigures/calc emit exactly one `ma1` line, and the generic settings
  // UI exposes its period (Inputs) plus colour/width/style/reset (Style), the
  // same surface every other single-line indicator (e.g. MA) gets.
  // Label the period "Period" (matching every other single-line indicator)
  // rather than "MA" — the Style tab already labels the line figure "MA", and
  // duplicating that name on the Inputs row was the source of the confusion.
  params: [periodParam('ma1', 'Period', 20, 200)],
  figures: [
    {
      key: 'volume',
      label: 'Volume',
      type: 'bar',
      defaultColor: PALETTE.vol,
      defaultUpColor: '#22c55e',
      defaultDownColor: '#ef4444',
      // Volume bars ship translucent so they recede behind the histogram MA and
      // don't over-saturate the sub-pane. The Style tab's opacity control still
      // overrides this per instance (seeds at 50%, user can push back to 100%).
      defaultOpacity: 0.5,
    },
    // Soft white (not PALETTE.ma1's loud orange): the volume MA sits quietly
    // above the bars. This defaultColor is the single source of truth for the
    // drawn line AND its legend-text color — `volumeIndicator.ts` deliberately
    // omits a per-figure color callback so this (or the user's Style-tab
    // override) wins. Keep it in sync with any legend-color expectations.
    // `hideable` gives the Style tab a visibility toggle so users can turn the
    // MA off and keep a pure histogram (same mechanism as Pivot Points' levels).
    // `opacity: 0.5` ships the MA line translucent by default so it sits quietly
    // above the bars (buildLineStyle folds it into the line color).
    line('ma1', 'MA', VOL_MA_COLOR, { hideable: true, opacity: 0.5 }),
  ],
}

// Registered by our own `macdIndicator.ts` (charts-core), not the klinecharts
// built-in: that one has no source / average-type inputs, doubles the
// histogram, and colours bars by sign only. Figure KEYS stay dif/dea/macd so
// saved style overrides keep resolving; new inputs are APPENDED so saved
// [fast, slow, signal] arrays keep their slots. Histogram listed first to match
// the draw order (bars under the lines).
const MACD: IndicatorDefinition = {
  name: 'MACD',
  label: 'MACD',
  description: 'Moving-average convergence/divergence.',
  paneType: 'sub',
  params: [
    periodParam('fast', 'Fast length', 12, 200),
    periodParam('slow', 'Slow length', 26, 200),
    periodParam('signal', 'Signal smoothing', 9, 200),
    {
      type: 'enum',
      key: 'source',
      label: 'Source',
      default: 'close',
      options: [
        { value: 'close', label: 'Close' },
        { value: 'open', label: 'Open' },
        { value: 'high', label: 'High' },
        { value: 'low', label: 'Low' },
        { value: 'hl2', label: '(H+L)/2' },
        { value: 'hlc3', label: '(H+L+C)/3' },
        { value: 'ohlc4', label: '(O+H+L+C)/4' },
      ],
    },
    {
      type: 'enum',
      key: 'oscMaType',
      label: 'Oscillator MA',
      description: 'Average used for the fast and slow lengths.',
      default: 'ema',
      options: [
        { value: 'ema', label: 'EMA' },
        { value: 'sma', label: 'SMA' },
      ],
    },
    {
      type: 'enum',
      key: 'signalMaType',
      label: 'Signal MA',
      description: 'Average used to smooth the MACD line into the signal line.',
      default: 'ema',
      options: [
        { value: 'ema', label: 'EMA' },
        { value: 'sma', label: 'SMA' },
      ],
    },
  ],
  figures: [
    {
      key: 'macd',
      label: 'Histogram',
      type: 'bar',
      defaultColor: MACD_HIST.growAbove,
      // The plain sign split, for anything that reads only up/down.
      defaultUpColor: MACD_HIST.growAbove,
      defaultDownColor: MACD_HIST.fallBelow,
      hideable: true,
      defaultHistogramColors: MACD_HIST,
    },
    line('dif', 'MACD', PALETTE.macd, { hideable: true }),
    line('dea', 'Signal', PALETTE.signal, { hideable: true }),
  ],
}

const KDJ: IndicatorDefinition = {
  name: 'KDJ',
  label: 'KDJ',
  description: 'Stochastic-style %K %D %J oscillator.',
  paneType: 'sub',
  params: [
    periodParam('period', 'Period', 9, 200),
    periodParam('k', 'K Smoothing', 3, 100),
    periodParam('d', 'D Smoothing', 3, 100),
  ],
  figures: [line('k', 'K', PALETTE.k), line('d', 'D', PALETTE.d), line('j', 'J', PALETTE.j)],
}

const RSI: IndicatorDefinition = {
  name: 'RSI',
  label: 'RSI',
  description: 'Relative strength index (Wilder smoothing, standard 14-period).',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 14, 200)],
  figures: [line('rsi1', 'RSI', PALETTE.rsi)],
}

const BIAS: IndicatorDefinition = {
  name: 'BIAS',
  label: 'BIAS',
  description: 'Deviation of price from its moving average.',
  paneType: 'sub',
  params: [
    periodParam('p1', 'Period 1', 6, 200),
    periodParam('p2', 'Period 2', 12, 200),
    periodParam('p3', 'Period 3', 24, 200),
  ],
  figures: [
    line('b1', 'BIAS 1', PALETTE.ma1),
    line('b2', 'BIAS 2', PALETTE.ma2),
    line('b3', 'BIAS 3', PALETTE.ma3),
  ],
}

const BRAR: IndicatorDefinition = {
  name: 'BRAR',
  label: 'BRAR',
  description: 'Buying/selling sentiment oscillator.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 26, 200)],
  figures: [line('br', 'BR', PALETTE.ma1), line('ar', 'AR', PALETTE.ma2)],
}

const CCI: IndicatorDefinition = {
  name: 'CCI',
  label: 'CCI',
  description: 'Commodity channel index.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 13, 200)],
  figures: [line('cci', 'CCI', PALETTE.ma1)],
}

const DMI: IndicatorDefinition = {
  name: 'DMI',
  label: 'DMI',
  description: 'Directional movement index with ADX.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 14, 200), periodParam('adx', 'ADX', 6, 100)],
  figures: [
    line('pdi', '+DI', PALETTE.up),
    line('mdi', '-DI', PALETTE.down),
    line('adx', 'ADX', PALETTE.ma1),
    line('adxr', 'ADXR', PALETTE.ma2),
  ],
}

const CR: IndicatorDefinition = {
  name: 'CR',
  label: 'CR',
  description: 'Price strength oscillator with MA overlays.',
  paneType: 'sub',
  params: [
    periodParam('period', 'Period', 26, 200),
    periodParam('ma1', 'MA 1', 10, 100),
    periodParam('ma2', 'MA 2', 20, 100),
    periodParam('ma3', 'MA 3', 40, 200),
    periodParam('ma4', 'MA 4', 62, 200),
  ],
  figures: [
    line('cr', 'CR', PALETTE.ma1),
    line('ma1', 'MA 1', PALETTE.ma2),
    line('ma2', 'MA 2', PALETTE.ma3),
    line('ma3', 'MA 3', PALETTE.ma4),
    line('ma4', 'MA 4', PALETTE.ma5),
  ],
}

const PSY: IndicatorDefinition = {
  name: 'PSY',
  label: 'PSY',
  description: 'Psychological line — share of rising periods.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 12, 100), periodParam('ma', 'MA', 6, 100)],
  figures: [line('psy', 'PSY', PALETTE.ma1), line('psyma', 'PSY MA', PALETTE.ma2)],
}

const DMA: IndicatorDefinition = {
  name: 'DMA',
  label: 'DMA',
  description: 'Difference of moving averages.',
  paneType: 'sub',
  params: [
    periodParam('fast', 'Fast', 10, 200),
    periodParam('slow', 'Slow', 50, 500),
    periodParam('ama', 'AMA', 10, 200),
  ],
  figures: [line('dif', 'DIF', PALETTE.ma1), line('difma', 'DIFMA', PALETTE.ma2)],
}

const TRIX: IndicatorDefinition = {
  name: 'TRIX',
  label: 'TRIX',
  description: 'Triple exponential moving average rate of change.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 12, 200), periodParam('ma', 'MA', 20, 200)],
  figures: [line('trix', 'TRIX', PALETTE.ma1), line('trma', 'TRMA', PALETTE.ma2)],
}

const OBV: IndicatorDefinition = {
  name: 'OBV',
  label: 'OBV',
  description: 'On-balance volume.',
  paneType: 'sub',
  params: [periodParam('ma', 'MA', 30, 200)],
  figures: [line('obv', 'OBV', PALETTE.ma1), line('maobv', 'MA OBV', PALETTE.ma2)],
}

const VR: IndicatorDefinition = {
  name: 'VR',
  label: 'VR',
  description: 'Volume ratio.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 24, 200), periodParam('ma', 'MA', 30, 200)],
  figures: [line('vr', 'VR', PALETTE.ma1), line('mavr', 'MA VR', PALETTE.ma2)],
}

const WR: IndicatorDefinition = {
  name: 'WR',
  label: 'Williams %R',
  description: 'Williams overbought/oversold oscillator (−100…0, standard 14-period).',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 14, 200)],
  figures: [line('wr1', '%R', PALETTE.ma1)],
}

const MTM: IndicatorDefinition = {
  name: 'MTM',
  label: 'Momentum',
  description: 'Rate-of-change momentum with MA.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 6, 200), periodParam('ma', 'MA', 10, 200)],
  figures: [line('mtm', 'MTM', PALETTE.ma1), line('mtmma', 'MA', PALETTE.ma2)],
}

const EMV: IndicatorDefinition = {
  name: 'EMV',
  label: 'EMV',
  description: 'Ease of movement.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 14, 200), periodParam('ma', 'MA', 9, 200)],
  figures: [line('emv', 'EMV', PALETTE.ma1), line('maemv', 'MA EMV', PALETTE.ma2)],
}

const ROC: IndicatorDefinition = {
  name: 'ROC',
  label: 'ROC',
  description: 'Rate of change.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 12, 200), periodParam('ma', 'MA', 6, 200)],
  figures: [line('roc', 'ROC', PALETTE.ma1), line('maroc', 'MA ROC', PALETTE.ma2)],
}

const PVT: IndicatorDefinition = {
  name: 'PVT',
  label: 'PVT',
  description: 'Price-volume trend.',
  paneType: 'sub',
  params: [],
  figures: [line('pvt', 'PVT', PALETTE.ma1)],
}

const AO: IndicatorDefinition = {
  name: 'AO',
  label: 'Awesome Oscillator',
  description: 'Bill Williams Awesome Oscillator (5/34 median diff).',
  paneType: 'sub',
  params: [periodParam('fast', 'Fast', 5, 200), periodParam('slow', 'Slow', 34, 500)],
  figures: [
    {
      key: 'ao',
      label: 'AO',
      type: 'bar',
      defaultColor: PALETTE.histPos,
      defaultUpColor: PALETTE.histPos,
      defaultDownColor: PALETTE.histNeg,
    },
  ],
}

/* ------------------------------------------------------------------ */
/*  TA-Lib parity indicators (registered in charts-core                */
/*  utils/talibIndicators.ts — param order must match calcParams)      */
/* ------------------------------------------------------------------ */

const PPO: IndicatorDefinition = {
  name: 'PPO',
  label: 'Percentage Price Oscillator',
  description: 'Percentage price oscillator — MACD normalized to percent of the slow EMA.',
  paneType: 'sub',
  params: [
    periodParam('fast', 'Fast', 12, 200),
    periodParam('slow', 'Slow', 26, 200),
    periodParam('signal', 'Signal', 9, 200),
  ],
  figures: [
    line('ppo', 'PPO', PALETTE.ma1),
    line('signal', 'Signal', PALETTE.signal),
    {
      key: 'hist',
      label: 'Histogram',
      type: 'bar',
      defaultColor: PALETTE.histPos,
      defaultUpColor: PALETTE.histPos,
      defaultDownColor: PALETTE.histNeg,
    },
  ],
}

const APO: IndicatorDefinition = {
  name: 'APO',
  label: 'Absolute Price Oscillator',
  description: 'Absolute price oscillator — fast EMA minus slow EMA in price units.',
  paneType: 'sub',
  params: [periodParam('fast', 'Fast', 12, 200), periodParam('slow', 'Slow', 26, 200)],
  figures: [line('apo', 'APO', PALETTE.ma1)],
}

const BOP: IndicatorDefinition = {
  name: 'BOP',
  label: 'Balance of Power',
  description: 'Close-vs-open position inside each bar’s range (−1 to +1).',
  paneType: 'sub',
  params: [],
  figures: [line('bop', 'BOP', PALETTE.ma1)],
}

const CMO: IndicatorDefinition = {
  name: 'CMO',
  label: 'Chande Momentum Oscillator',
  description: 'Chande momentum oscillator — net gains vs total movement (−100 to +100).',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 14, 200)],
  figures: [line('cmo', 'CMO', PALETTE.ma1)],
}

const IMI: IndicatorDefinition = {
  name: 'IMI',
  label: 'Intraday Momentum Index',
  description: 'Intraday momentum index — RSI computed on candle bodies (open vs close).',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 14, 200)],
  figures: [line('imi', 'IMI', PALETTE.ma1)],
}

const ULTOSC: IndicatorDefinition = {
  name: 'ULTOSC',
  label: 'Ultimate Oscillator',
  description: 'Buying pressure over three weighted lookbacks (default 7/14/28).',
  paneType: 'sub',
  params: [
    periodParam('p1', 'Fast', 7, 200),
    periodParam('p2', 'Medium', 14, 200),
    periodParam('p3', 'Slow', 28, 500),
  ],
  figures: [line('ultosc', 'ULTOSC', PALETTE.ma1)],
}

const STOCHF: IndicatorDefinition = {
  name: 'STOCHF',
  label: 'Stochastic Fast',
  description: 'Fast stochastic — raw %K with a short SMA %D, no slow smoothing.',
  paneType: 'sub',
  params: [periodParam('k', '%K Period', 14, 200), periodParam('d', '%D Period', 3, 100)],
  figures: [line('k', '%K', PALETTE.k), line('d', '%D', PALETTE.d)],
}

const AROON: IndicatorDefinition = {
  name: 'AROON',
  label: 'Aroon',
  description: 'Bars since the highest high / lowest low, scaled 0–100.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 14, 200)],
  figures: [line('up', 'Up', PALETTE.up), line('down', 'Down', PALETTE.down)],
}

const AROONOSC: IndicatorDefinition = {
  name: 'AROONOSC',
  label: 'Aroon Oscillator',
  description: 'Aroon Up minus Aroon Down as a single line (−100 to +100).',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 14, 200)],
  figures: [line('osc', 'Oscillator', PALETTE.ma1)],
}

const MFI: IndicatorDefinition = {
  name: 'MFI',
  label: 'Money Flow Index',
  description: 'Money flow index — volume-weighted RSI of typical price.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 14, 200)],
  figures: [line('mfi', 'MFI', PALETTE.ma1)],
}

const STOCHRSI: IndicatorDefinition = {
  name: 'STOCHRSI',
  label: 'Stochastic RSI',
  description: 'Stochastic oscillator applied to RSI values instead of price.',
  paneType: 'sub',
  params: [
    periodParam('rsi', 'RSI Period', 14, 200),
    periodParam('stoch', 'Stoch Period', 14, 200),
    periodParam('k', '%K Smoothing', 3, 100),
    periodParam('d', '%D Smoothing', 3, 100),
  ],
  figures: [line('k', '%K', PALETTE.k), line('d', '%D', PALETTE.d)],
}

const ATR: IndicatorDefinition = {
  name: 'ATR',
  label: 'Average True Range',
  description: 'Average true range — Wilder-smoothed bar range in price units.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 14, 200)],
  figures: [line('atr', 'ATR', PALETTE.ma1)],
}

const NATR: IndicatorDefinition = {
  name: 'NATR',
  label: 'Normalized Average True Range',
  description: 'Normalized ATR — true range as a percent of close, comparable across tickers.',
  paneType: 'sub',
  params: [periodParam('period', 'Period', 14, 200)],
  figures: [line('natr', 'NATR', PALETTE.ma1)],
}

const AD: IndicatorDefinition = {
  name: 'AD',
  label: 'Chaikin Accumulation/Distribution',
  description: 'Chaikin accumulation/distribution line — cumulative close-location volume.',
  paneType: 'sub',
  params: [],
  figures: [line('ad', 'A/D', PALETTE.ma1)],
}

const ADOSC: IndicatorDefinition = {
  name: 'ADOSC',
  label: 'Chaikin Oscillator',
  description: 'Fast minus slow EMA of the accumulation/distribution line (default 3/10).',
  paneType: 'sub',
  params: [periodParam('fast', 'Fast', 3, 100), periodParam('slow', 'Slow', 10, 200)],
  figures: [line('adosc', 'ADOSC', PALETTE.ma1)],
}

/* ------------------------------------------------------------------ */
/*  Catalog                                                             */
/* ------------------------------------------------------------------ */

export const INDICATOR_CATALOG: IndicatorCatalog = Object.freeze({
  MA,
  EMA,
  SMA,
  BOLL,
  SAR,
  BBI,
  VWAP,
  PIVOT,
  POSITION_SIZE,
  VPVR,
  VPSV,
  VPXD,
  HTF,
  WMA,
  HMA,
  DEMA,
  TEMA,
  TMA,
  KAMA,
  VIDYA,
  ZLEMA,
  MGD,
  WWS,
  KC,
  DC,
  ENV,
  HLB,
  APZ,
  LINREG,
  LRI,
  TSF,
  LRS,
  LRA,
  VOL,
  MACD,
  KDJ,
  RSI,
  BIAS,
  BRAR,
  CCI,
  DMI,
  CR,
  PSY,
  DMA,
  TRIX,
  OBV,
  VR,
  WR,
  MTM,
  EMV,
  ROC,
  PVT,
  AO,
  PPO,
  APO,
  BOP,
  CMO,
  IMI,
  ULTOSC,
  STOCHF,
  AROON,
  AROONOSC,
  MFI,
  STOCHRSI,
  ATR,
  NATR,
  AD,
  ADOSC,
})

export const MAIN_INDICATOR_DEFS: IndicatorDefinition[] = Object.values(INDICATOR_CATALOG).filter(
  (d) => d.paneType === 'main',
)
export const SUB_INDICATOR_DEFS: IndicatorDefinition[] = Object.values(INDICATOR_CATALOG).filter(
  (d) => d.paneType === 'sub',
)

export function getIndicatorDef(name: string): IndicatorDefinition | undefined {
  return INDICATOR_CATALOG[name]
}

/** Returns the default calcParams array, ordered to match the indicator's param schema. */
/**
 * Repair a saved calcParams array: every missing or null slot (up to the
 * catalog's param count) takes its catalog default. klinecharts replaces
 * calcParams wholesale, so a short array like `[15]` (what the settings drawer
 * used to emit for a study added without params) NaNs a multi-input calc and
 * the pane loads blank. `undefined` stays undefined (the template defaults
 * apply); a complete array comes back as the same reference.
 */
export function completeCalcParams(
  name: string,
  params: unknown[] | undefined,
): unknown[] | undefined {
  const def = INDICATOR_CATALOG[name]
  if (!params || !def) return params
  if (def.params.every((_, i) => params[i] != null)) return params
  const out = [...params]
  def.params.forEach((p, i) => {
    if (out[i] == null) out[i] = p.type === 'bool' ? Boolean(p.default) : p.default
  })
  return out
}

export function getDefaultCalcParams(name: string): unknown[] | undefined {
  const def = INDICATOR_CATALOG[name]
  if (!def || def.params.length === 0) return undefined
  return def.params.map((p) => (p.type === 'bool' ? Boolean(p.default) : p.default))
}

/**
 * Reorder a SAR whose calcParams were saved before the slot-order fix. The
 * catalog then listed its inputs as Step, Max Step and Min Step, so the settings
 * drawer and the AI indicator tool wrote [step, max, start] (Min Step is the
 * starting factor), which klinecharts reads as [start, step, max]: [2, 20, 2]
 * pinned the acceleration factor at 0.02. The result is [start, step, max].
 *
 * A correct save has step <= max, and an old one read in klinecharts order has
 * step > max, so only old saves match. Run this where saved state is LOADED,
 * never on live edits, where a Step above Max is the user's own input.
 */
export function remapLegacySarParams(
  name: string,
  params: unknown[] | undefined,
): unknown[] | undefined {
  if (name !== 'SAR' || params?.length !== 3) return params
  if (!params.every((p) => typeof p === 'number' && Number.isFinite(p))) return params
  const [step, max, start] = params as number[]
  // `step <= max` keeps this idempotent: without it the output would match again
  // whenever the old Step was above the old Max Step, and flip on every load.
  return max > start && step <= max ? [start, step, max] : params
}

/** Saved state that keeps indicator lists: a panel, a chart template, a backtest cell. */
interface SavedIndicatorLists {
  mainIndicators?: unknown
  subIndicators?: unknown
}

function remapLegacySarEntry(entry: unknown): unknown {
  if (typeof entry !== 'object' || entry === null) return entry
  const { name, calcParams } = entry as { name?: unknown; calcParams?: unknown }
  if (typeof name !== 'string' || !Array.isArray(calcParams)) return entry
  const remapped = remapLegacySarParams(name, calcParams)
  return remapped === calcParams ? entry : { ...entry, calcParams: remapped }
}

function remapLegacySarList(list: unknown): unknown {
  if (!Array.isArray(list)) return list
  const remapped = list.map(remapLegacySarEntry)
  return remapped.some((entry, i) => entry !== list[i]) ? remapped : list
}

/**
 * {@link remapLegacySarParams} over the indicator lists of a saved state. Hands
 * back the same object, lists and entries when nothing needs reordering, so a
 * load that finds nothing to do changes no identity.
 */
export function remapLegacySarIndicators<S extends SavedIndicatorLists>(saved: S): S {
  const main = remapLegacySarList(saved.mainIndicators)
  const sub = remapLegacySarList(saved.subIndicators)
  if (main === saved.mainIndicators && sub === saved.subIndicators) return saved
  return {
    ...saved,
    ...(main !== saved.mainIndicators ? { mainIndicators: main } : {}),
    ...(sub !== saved.subIndicators ? { subIndicators: sub } : {}),
  }
}
